import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarRange,
  CheckCircle2,
  DoorOpen,
  Inbox,
  Lock,
  MessageSquare,
} from 'lucide-react';
import { Link } from 'react-router';
import { api, type Pagina } from '@/shared/api/cliente';
import type { AperturaDeCaja, EstadoDeLaCaja } from '@/shared/api/tipos';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { ModalDeApertura } from './ModalDeApertura';
import { enFecha, enMomento, hace, hoy } from './fechas';

export function PaginaAperturaDeCaja() {
  const { puede } = useSesion();
  const clienteDeConsultas = useQueryClient();

  const revisa = puede('caja.revisar');
  const abreCaja = puede('caja.abrir');

  const [desde, setDesde] = useState(hace(29));
  const [hasta, setHasta] = useState(hoy());
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [abriendo, setAbriendo] = useState(false);

  const estado = useQuery({
    queryKey: ['caja-estado'],
    queryFn: () => api.get<EstadoDeLaCaja>('/aperturas/estado'),

    enabled: abreCaja,
  });

  const aperturas = useQuery({
    queryKey: ['aperturas', revisa, desde, hasta, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<AperturaDeCaja>>(revisa ? '/aperturas' : '/aperturas/mias', {
        query: { desde, hasta, pagina, tamanio },
      }),
  });

  const e = estado.data;

  const columnas: Columna<AperturaDeCaja>[] = [
    {
      clave: 'fecha',
      encabezado: 'Fecha',
      contenido: (a) => (
        <span className="block min-w-0">
          <span className="importe block text-xs font-semibold text-armazon-800">
            {enFecha(a.fecha)}
          </span>
          <span className="block truncate text-[10px] uppercase text-armazon-400">
            Abierta {enMomento(a.abiertoEn)}
          </span>
        </span>
      ),
    },
    {
      clave: 'cajero',
      encabezado: 'Cajero',
      contenido: (a) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-700">
            {a.nombreDelCajero}
          </span>
          <span className="block text-[10px] uppercase text-armazon-400">
            {a.caja ? `Caja ${a.caja}` : 'Sin caja asignada'}
          </span>
        </span>
      ),
    },
    {
      clave: 'contado',
      encabezado: 'Fondo contado',
      alinear: 'derecha',
      contenido: (a) => (
        <span className="importe text-xs font-semibold text-armazon-800">
          ${a.fondoInicial.toFixed(2)}
        </span>
      ),
    },
    {
      clave: 'esperado',
      encabezado: 'Requerido',
      alinear: 'derecha',
      contenido: (a) => (
        <span className="importe text-xs text-armazon-400">
          {a.fondoEsperado > 0 ? `$${a.fondoEsperado.toFixed(2)}` : '—'}
        </span>
      ),
    },
    {
      clave: 'diferencia',
      encabezado: 'Diferencia',
      alinear: 'derecha',
      contenido: (a) => <Descuadre apertura={a} />,
    },
    {
      clave: 'nota',
      encabezado: '',
      alinear: 'centro',
      clase: 'w-10',

      contenido: (a) =>
        a.motivoDiferencia || a.observaciones ? (
          <span
            title={[a.motivoDiferencia, a.observaciones].filter(Boolean).join(' · ')}
            className="inline-grid size-7 place-items-center rounded-lg text-armazon-400"
          >
            <MessageSquare className="size-3.5" aria-hidden />
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={DoorOpen}
        color="pizarra"
        titulo="Apertura de caja"
        subtitulo="Registrar el fondo inicial y dejar constancia de la apertura"
        acciones={
          e && !e.abierta && !e.yaCerrada ? (
            <button
              type="button"
              onClick={() => setAbriendo(true)}
              title="Abrir la caja"
              aria-label="Abrir la caja"
              className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <DoorOpen className="size-5" aria-hidden />
            </button>
          ) : null
        }
      />

      {abreCaja && <EstadoDeHoy estado={e} cargando={estado.isPending} />}

      <Tabla
        datos={aperturas.data}
        cargando={aperturas.isPending}

        columnas={revisa ? columnas : columnas.filter((c) => c.clave !== 'cajero')}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="aperturas"
        iconoVacio={DoorOpen}
        mensajeVacio={
          revisa ? 'No hay aperturas en esas fechas' : 'Todavía no ha abierto ninguna caja'
        }
        herramientas={
          <Fechas
            desde={desde}
            hasta={hasta}
            alCambiar={(d, h) => {
              setDesde(d);
              setHasta(h);
              setPagina(0);
            }}
          />
        }
      />

      {abriendo && e && (
        <ModalDeApertura
          estado={e}
          alCancelar={() => setAbriendo(false)}
          alAbierta={() => {
            setAbriendo(false);
            void clienteDeConsultas.invalidateQueries({ queryKey: ['aperturas'] });
          }}
        />
      )}
    </>
  );
}

function EstadoDeHoy({
  estado,
  cargando,
}: {
  estado: EstadoDeLaCaja | undefined;
  cargando: boolean;
}) {
  if (cargando) {
    return (
      <div className="mb-4 rounded-2xl border border-armazon-200 bg-white">
        <CargandoEnLinea detalle="Mirando la caja" />
      </div>
    );
  }
  if (!estado) {
    return null;
  }

  if (estado.yaCerrada) {
    return (
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-2xl border border-armazon-200 bg-armazon-50 p-4">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-armazon-200 text-armazon-600">
          <Lock className="size-5" aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-bold uppercase text-armazon-800">
            Su caja de hoy ya está cerrada
          </span>
          <span className="block text-[11px] uppercase text-armazon-500">
            Un turno cerrado no se reabre: lo cobrado después no estaría en el arqueo firmado.
          </span>
        </span>
      </div>
    );
  }

  const a = estado.apertura;

  if (estado.abierta && a) {
    const cuadra = a.diferencia === 0;
    return (
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-marca-200 bg-white p-4 shadow-sm">
        <span className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-marca-50 text-marca-600">
            <CheckCircle2 className="size-5" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-bold uppercase text-armazon-800">
              Su caja está abierta
            </span>
            <span className="block text-[11px] uppercase text-armazon-500">
              {a.nombreDelCajero}
              {a.caja && ` · Caja ${a.caja}`} · {enMomento(a.abiertoEn)}
            </span>
          </span>
        </span>

        <span className="ml-auto flex flex-wrap items-center gap-x-6 gap-y-2">
          <Cifra rotulo="Fondo contado" valor={a.fondoInicial} />
          {a.fondoEsperado > 0 && (
            <>
              <Cifra rotulo="Requerido" valor={a.fondoEsperado} />
              <span className="text-right">
                <span
                  className={`block text-[10px] font-semibold uppercase tracking-wide ${
                    cuadra ? 'text-marca-700' : 'text-red-700'
                  }`}
                >
                  {cuadra ? 'Cuadró' : a.diferencia > 0 ? 'Sobró' : 'Faltó'}
                </span>
                <span
                  className={`importe block text-lg font-bold ${
                    cuadra ? 'text-marca-700' : 'text-red-700'
                  }`}
                >
                  ${Math.abs(a.diferencia).toFixed(2)}
                </span>
              </span>
            </>
          )}
          <Link
            to="/recaudacion/cobros"
            className="rounded-lg border border-armazon-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600 transition-colors hover:border-marca-300 hover:text-marca-700"
          >
            Ir a cobrar
          </Link>
        </span>
      </div>
    );
  }

  return (
    <div className="relative mb-4 flex flex-wrap items-center gap-x-8 gap-y-3 overflow-hidden rounded-2xl bg-armazon-900 p-5 text-white shadow-sm">
      <span className="min-w-0">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-white/60">
          Debe haber al abrir
        </span>
        <span className="importe block text-3xl font-bold">${estado.fondoEsperado.toFixed(2)}</span>
        <span className="block text-[11px] uppercase text-white/60">
          {estado.fondoEsperado > 0 ? 'Fondo de apertura requerido' : 'Sin fondo configurado'}
          {estado.caja && ` · Caja ${estado.caja}`}
        </span>
      </span>

      <span className="min-w-0 text-[11px] uppercase leading-relaxed text-white/60">
        Su caja de hoy todavía no está abierta.
        <br />
        Hasta que lo esté, la ventanilla no admite cobros.
      </span>

      <Inbox className="absolute -bottom-3 right-4 size-20 text-white/5" aria-hidden />
    </div>
  );
}

function Fechas({
  desde,
  hasta,
  alCambiar,
}: {
  desde: string;
  hasta: string;
  alCambiar: (desde: string, hasta: string) => void;
}) {
  return (
    <span className="flex items-center gap-2">
      <CalendarRange className="size-4 shrink-0 text-armazon-400" aria-hidden />
      <input
        type="date"
        value={desde}
        max={hasta}
        onChange={(evento) => alCambiar(evento.target.value, hasta)}
        aria-label="Desde"
        className="importe h-9 rounded-lg border border-armazon-200 px-2 text-xs outline-none transition-colors focus:border-marca-400"
      />
      <span className="text-[10px] uppercase text-armazon-400">a</span>
      <input
        type="date"
        value={hasta}
        min={desde}
        onChange={(evento) => alCambiar(desde, evento.target.value)}
        aria-label="Hasta"
        className="importe h-9 rounded-lg border border-armazon-200 px-2 text-xs outline-none transition-colors focus:border-marca-400"
      />
    </span>
  );
}

function Descuadre({ apertura }: { apertura: AperturaDeCaja }) {
  if (apertura.fondoEsperado <= 0) {
    return <span className="text-xs text-armazon-300">—</span>;
  }
  if (apertura.diferencia === 0) {
    return <span className="importe text-xs text-armazon-400">$0.00</span>;
  }

  const sobra = apertura.diferencia > 0;

  return (
    <span
      title={apertura.motivoDiferencia ?? undefined}
      className={`importe inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-bold ${
        sobra ? 'bg-blue-50 text-blue-800' : 'bg-red-50 text-red-800'
      }`}
    >
      {sobra ? '+' : '−'}${Math.abs(apertura.diferencia).toFixed(2)}
      <AlertCircle className="size-3" aria-hidden />
    </span>
  );
}

function Cifra({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <span className="text-right">
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </span>
      <span className="importe block text-lg font-bold text-armazon-900">${valor.toFixed(2)}</span>
    </span>
  );
}
